import { useState, useEffect } from 'react';
import { catsApi } from '../utils/api';
import { handleApiError } from '../utils/helpers';
import CatCard from '../components/CatCard';

const AllCatsPage = () => {
  const [cats, setCats] = useState([]);
  const [catsCount, setCatsCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterGender, setFilterGender] = useState('');
  
  // Fetch cats on component mount
  useEffect(() => {
    fetchCats();
    fetchCatsCount();
  }, []);
  
  // Fetch cats from the API
  const fetchCats = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const catsData = await catsApi.getAllCats();
      setCats(catsData);
    } catch (error) {
      console.error('Error fetching cats:', error);
      setError(handleApiError(error));
    } finally {
      setLoading(false);
    }
  };
  
  // Fetch total cats count
  const fetchCatsCount = async () => {
    try {
      const countData = await catsApi.getCatsCount();
      console.log('Cats count API response:', countData);
      const count = countData.count || 0;
      console.log('Setting cats count to:', count);
      setCatsCount(count);
    } catch (error) {
      console.error('Error fetching cats count:', error);
      // We don't set the main error state here to not interrupt the main UI flow
    }
  };
  
  // Handle feed request success
  const handleFeedRequestSuccess = (catId) => {
    setCats((prevCats) => 
      prevCats.map((cat) => 
        cat.id === catId 
          ? { ...cat, pendingRequests: (cat.pendingRequests || 0) + 1 } 
          : cat
      )
    );
  };
  
  // Filter cats based on search and gender filter
  const filteredCats = cats.filter((cat) => {
    const matchesSearch = searchQuery 
      ? cat.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (cat.description && cat.description.toLowerCase().includes(searchQuery.toLowerCase()))
      : true;
      
    const matchesGender = filterGender 
      ? cat.gender === filterGender 
      : true;
      
    return matchesSearch && matchesGender;
  });
  
  // Reset filters
  const handleResetFilters = () => {
    setSearchQuery('');
    setFilterGender('');
  };
  
  return (
    <div className="all-cats-page">
      <div className="page-header">
        <h1 className="page-title">All Cats</h1>
        
        {/* Total Cats Count - Very Prominent */}
        <div className="total-cats-count">
          Total Cats in System: {loading ? (
            <span className="count-value">Loading...</span>
          ) : (
            <span className="count-value">{catsCount}</span>
          )}
          <button 
            className="refresh-count-button"
            onClick={fetchCatsCount}
            title="Refresh cat count"
          >
            🔄
          </button>
        </div>
        
        <p className="page-description">
          Browse all the cats looking for a meal and request to feed them
        </p>
      </div>
      
      {/* Debug Information - To help diagnose issues */}
      <div style={{
        padding: '8px 16px',
        backgroundColor: '#f8f9fa',
        borderRadius: '4px',
        marginBottom: '16px',
        fontSize: '14px',
        display: 'flex',
        flexDirection: 'column',
        gap: '4px'
      }}>
        <p><strong>Debug Info:</strong></p>
        <p>API Response Cat Count: {catsCount}</p>
        <p>Loading State: {loading ? 'Loading...' : 'Completed'}</p>
        <p>Number of Cats Loaded: {cats.length}</p>
      </div>
      
      {/* Filter section */}
      <div className="filter-bar">
        <div className="filter-form">
          <div className="filter-group">
            <label htmlFor="search">Search Cats</label>
            <input
              type="text"
              id="search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by name or description"
            />
          </div>
          
          <div className="filter-group">
            <label htmlFor="gender">Filter by Gender</label>
            <select
              id="gender"
              value={filterGender}
              onChange={(e) => setFilterGender(e.target.value)}
            >
              <option value="">All</option>
              <option value="M">Male</option>
              <option value="F">Female</option>
            </select>
          </div>
          
          <div className="filter-actions">
            <button 
              className="button secondary"
              onClick={handleResetFilters}
            >
              Reset Filters
            </button>
          </div>
        </div>
      </div>
      
      {/* Loading state */}
      {loading && (
        <div className="loading-container">
          <div className="loading-spinner"></div>
        </div>
      )}
      
      {/* Error state */}
      {error && !loading && (
        <div className="error-message">
          <p>{error}</p>
          <button 
            className="button"
            onClick={fetchCats}
          >
            Try Again
          </button>
        </div>
      )}
      
      {/* Empty state */}
      {!loading && !error && filteredCats.length === 0 && (
        <div className="empty-state">
          <div className="empty-state-icon">🐱</div>
          <h2 className="empty-state-title">No cats found</h2>
          <p className="empty-state-description">
            {searchQuery || filterGender 
              ? "No cats match your current filters. Try adjusting your search criteria." 
              : "There are no cats available at the moment. Please check back later."}
          </p>
          {(searchQuery || filterGender) && (
            <button 
              className="button"
              onClick={handleResetFilters}
            >
              Clear Filters
            </button>
          )}
        </div>
      )}
      
      {/* Cats grid */}
      {!loading && !error && filteredCats.length > 0 && (
        <div className="cards-grid">
          {filteredCats.map((cat) => (
            <CatCard
              key={cat.id}
              cat={cat}
              onRequestSuccess={handleFeedRequestSuccess}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default AllCatsPage; 